// ============================================================
// home-search/city-suggestions.js — Sugerencias de ciudades
// ============================================================

import { normalizeText, debounce } from './utils.js';

// Ciudades colombianas populares para autocompletado offline
export const POPULAR_CO_CITIES = [
  'Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga',
  'Pereira', 'Cúcuta', 'Santa Marta', 'Ibagué', 'Manizales', 'Pasto',
  'Montería', 'Valledupar', 'Villavicencio', 'Neiva', 'Sincelejo', 'Armenia',
  'Tunja', 'Popayán', 'Buenaventura', 'Soacha', 'Ocaña'
];

const CITY_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 horas
const CITY_CACHE_MAX = 40;
const CITY_CACHE_KEY = 'co_city_cache_v1';

let cityCache = new Map(); // key: normalized query, value: { ts, results }

// Cargar cache desde localStorage
(function initCityCache() {
  try {
    const raw = localStorage.getItem(CITY_CACHE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      const now = Date.now();
      arr.forEach(entry => {
        if (entry && entry.q && entry.results && now - (entry.ts || 0) < CITY_CACHE_TTL_MS) {
          cityCache.set(entry.q, { ts: entry.ts, results: entry.results });
        }
      });
    }
  } catch (_) {}
})();

function persistCityCache() {
  try {
    const items = Array.from(cityCache.entries())
      .sort((a, b) => (b[1].ts || 0) - (a[1].ts || 0))
      .slice(0, CITY_CACHE_MAX)
      .map(([q, v]) => ({ q, ts: v.ts, results: v.results }));
    localStorage.setItem(CITY_CACHE_KEY, JSON.stringify(items));
  } catch (_) {}
}

function getPopularMatches(q) {
  const nq = normalizeText(q);
  return POPULAR_CO_CITIES
    .filter(name => normalizeText(name).startsWith(nq))
    .slice(0, 8)
    .map(name => ({ name, display_name_short: name }));
}

function limpiarCitySugs(citySuggestions) {
  if (!citySuggestions) return;
  citySuggestions.replaceChildren();
  citySuggestions.style.display = 'none';
}

function renderCitySuggestions(citySuggestions, list, inputCiudad) {
  if (!Array.isArray(list) || list.length === 0) {
    limpiarCitySugs(citySuggestions);
    return;
  }
  citySuggestions.replaceChildren();
  citySuggestions.style.display = 'block';

  list.forEach(c => {
    const el = document.createElement('div');
    el.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;cursor:pointer;';

    const icon = document.createElement('i');
    icon.className = 'fa fa-location-dot';
    icon.style.cssText = 'color:#2563eb;width:16px;text-align:center;';

    const span = document.createElement('span');
    span.style.cssText = 'font-size:0.95rem;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(100% - 20px);';
    const cityName = c.display_name_short || c.name || c.display_name || '';
    const finalText = `${cityName}, Colombia`;
    span.textContent = finalText;

    el.appendChild(icon);
    el.appendChild(span);

    el.addEventListener('mouseenter', () => { el.style.background = '#f3f4f6'; });
    el.addEventListener('mouseleave', () => { el.style.background = 'transparent'; });
    el.addEventListener('click', () => {
      inputCiudad.value = finalText;
      try { localStorage.setItem('user_city', finalText); } catch(_) { }
      const ev = new Event('input');
      inputCiudad.dispatchEvent(ev);
      limpiarCitySugs(citySuggestions);
    });

    citySuggestions.appendChild(el);
  });
}

/**
 * Crea el contenedor de sugerencias de ciudad (position: fixed)
 * y configura los event listeners
 * @param {HTMLInputElement} inputCiudad
 * @returns {HTMLElement} citySuggestions container
 */
export function createCitySuggestions(inputCiudad) {
  let citySuggestions = document.getElementById('city-suggestions');
  if (!citySuggestions) {
    citySuggestions = document.createElement('div');
    citySuggestions.id = 'city-suggestions';
    citySuggestions.style.cssText = 'position:fixed;background:#fff;border:1px solid #e5e7eb;border-radius:0.75rem;box-shadow:0 16px 36px rgba(0,0,0,0.18);padding:6px;display:none;z-index:999999;max-height:300px;overflow-y:auto;white-space:nowrap;min-width:340px;';
    document.body.appendChild(citySuggestions);

    const placeCityDropdown = () => {
      if (!inputCiudad) return;
      requestAnimationFrame(() => {
        const r = inputCiudad.getBoundingClientRect();
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
        const extra = isMobile ? 18 : 8;
        citySuggestions.style.left = `${Math.round(r.left)}px`;
        citySuggestions.style.top = `${Math.round(r.bottom + extra)}px`;
        const desired = Math.max(Math.round(r.width), 360);
        citySuggestions.style.width = `${desired}px`;
      });
    };
    window.addEventListener('resize', placeCityDropdown);
    window.addEventListener('scroll', placeCityDropdown, true);
  }

  // Listener para cerrar al click fuera
  document.addEventListener('click', (e) => {
    if (citySuggestions && !citySuggestions.contains(e.target) && e.target !== inputCiudad) {
      limpiarCitySugs(citySuggestions);
    }
  });

  // Fetch de sugerencias al escribir
  const fetchCitySuggestions = debounce(() => {
    const q = (inputCiudad && inputCiudad.value || '').trim();
    if (!q || q.length < 2) {
      limpiarCitySugs(citySuggestions);
      return;
    }
    try {
      const key = normalizeText(q);
      const now = Date.now();

      const cached = cityCache.get(key);
      if (cached && now - cached.ts < CITY_CACHE_TTL_MS) {
        renderCitySuggestions(citySuggestions, cached.results, inputCiudad);
        return;
      }

      if (q.length < 3) {
        const popular = getPopularMatches(q);
        renderCitySuggestions(citySuggestions, popular, inputCiudad);
        return;
      }

      const popular = getPopularMatches(q);
      cityCache.set(key, { ts: now, results: popular });
      persistCityCache();
      renderCitySuggestions(citySuggestions, popular, inputCiudad);
    } catch (e) {
      limpiarCitySugs(citySuggestions);
    }
  }, 250);

  inputCiudad.addEventListener('input', fetchCitySuggestions);
  inputCiudad.addEventListener('focus', fetchCitySuggestions);

  return citySuggestions;
}
